import { useCallback, useMemo } from 'react';
import { STORAGE_KEY, useStorage } from '../contexts/StorageContext';

// ─── 날짜 키 유틸 ─────────────────────────────────────────────────────────────

/**
 * `YYYY-MM-DD` 형식의 오늘 날짜 문자열 (로컬 타임존 기준).
 *
 * 주의: toISOString() 은 UTC 기준이라 KST(UTC+9)에서는 오전 9시에 날짜가 바뀐다.
 * 데일리 작업 키는 작업자의 로컬 자정에 롤오버해야 하므로 로컬 날짜 부품으로 만든다.
 */
function todayStr(): string {
  const d = new Date();
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/**
 * 베이스 키에 매장 코드 + 오늘 날짜를 붙인 실제 localStorage 키를 반환한다.
 *
 * 매장 코드가 꼭 필요하다 — 예전엔 날짜만 키에 들어가서, 같은 브라우저에서 부산↔광주
 * 계정을 오가면(로그아웃 없이 SUPER가 매장을 전환하는 경우도 포함) 한 매장의 "하락 확인
 * 대기" 목록이 다른 매장 화면에 그대로 새어 들어갔다(2026-07-28, 사용자가 광주 667건이
 * 로그아웃 후 부산 계정에서도 그대로 보이는 걸 발견). 로그아웃 시에만 지우는 방식으로는
 * SUPER가 로그아웃 없이 매장만 바꾸는 경우를 못 막아서, 키 자체를 매장별로 분리하는
 * 쪽이 근본 수정이다.
 */
function dailyKey(base: string, storeCode: string): string {
  return `${base}:${storeCode}:${todayStr()}`;
}

/** pruneOldKeys가 정리 대상으로 보는 데일리 워크플로우 키 prefix 목록. */
const WORKFLOW_STORAGE_PREFIXES = [
  STORAGE_KEY.workflowUpdateDone,
  STORAGE_KEY.workflowNaverDone,
  STORAGE_KEY.workflowPendingIds,
  STORAGE_KEY.workflowStoreUnresolved,
];

// ─── useDailyWorkflowStorage ──────────────────────────────────────────────────

/**
 * 데일리 워크플로우 진행 상태의 localStorage 읽기/쓰기.
 *
 * 저장 전략:
 *   - 키 형식: `{baseKey}:{YYYY-MM-DD}` (예: `wf:updateDone:2025-06-17`)
 *   - 당일 날짜를 suffix 로 쓰기 때문에 별도 만료 로직이 없어도
 *     다음 날 로드 시 자동으로 빈 값으로 떨어진다.
 *   - 오래된 키(어제 이전)는 정리하지 않아도 용량 부담이 없다(최대 3개 × n일).
 *     필요 시 `pruneOldKeys()` 를 명시적으로 호출한다.
 *
 * 저장 대상:
 *   - updateDone  : boolean  — 오늘 수정가 조회 완료 여부
 *   - naverDone   : boolean  — 오늘 Naver 반영 완료 여부
 *   - pendingIds  : number[] — 하락 확인 대기 카드 id 목록 (리뷰 중 진행분 포함)
 *
 * 사용처: useCardData (useCardJobs / useInlinePriceEdit)
 */
export function useDailyWorkflowStorage() {
  // SUPER는 superStore(전환 대상)를 우선, ADMIN/VIEWER는 고정된 store를 쓴다 — http.ts의
  // X-Store-Code 판단 로직과 동일한 우선순위. 매장 선택 전(SUPER 전체 개요)엔 'none' 버킷을
  // 쓴다 — 그 화면은 카드별 작업 대상이 없어 워크플로우 상태 자체가 의미 없다.
  const { store, superStore } = useStorage();
  const activeStore = superStore || store || 'none';

  // ── 읽기 ────────────────────────────────────────────────────────────────────

  const readUpdateDone = useCallback((): boolean => {
    return localStorage.getItem(dailyKey(STORAGE_KEY.workflowUpdateDone, activeStore)) === 'true';
  }, [activeStore]);

  const readNaverDone = useCallback((): boolean => {
    return localStorage.getItem(dailyKey(STORAGE_KEY.workflowNaverDone, activeStore)) === 'true';
  }, [activeStore]);

  const readPendingIds = useCallback((): number[] => {
    try {
      const raw = localStorage.getItem(dailyKey(STORAGE_KEY.workflowPendingIds, activeStore));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as number[]) : [];
    } catch {
      return [];
    }
  }, [activeStore]);

  const readStoreUnresolved = useCallback((): number => {
    const n = Number(localStorage.getItem(dailyKey(STORAGE_KEY.workflowStoreUnresolved, activeStore)));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [activeStore]);

  // ── 쓰기 ────────────────────────────────────────────────────────────────────

  const saveStoreUnresolved = useCallback((n: number) => {
    localStorage.setItem(dailyKey(STORAGE_KEY.workflowStoreUnresolved, activeStore), String(n));
  }, [activeStore]);

  const saveUpdateDone = useCallback((done: boolean) => {
    localStorage.setItem(dailyKey(STORAGE_KEY.workflowUpdateDone, activeStore), String(done));
  }, [activeStore]);

  const saveNaverDone = useCallback((done: boolean) => {
    localStorage.setItem(dailyKey(STORAGE_KEY.workflowNaverDone, activeStore), String(done));
  }, [activeStore]);

  const savePendingIds = useCallback((ids: Set<number>) => {
    localStorage.setItem(
      dailyKey(STORAGE_KEY.workflowPendingIds, activeStore),
      JSON.stringify(Array.from(ids)),
    );
  }, [activeStore]);

  // ── 오래된 키 정리 (선택적) ──────────────────────────────────────────────────
  // 어제 이전의 wf:* 키를 한꺼번에 삭제한다(매장 구분 없이 전부) — 매 로그인 시 한 번만
  // 호출하면 충분하다.
  const pruneOldKeys = useCallback(() => {
    const today = todayStr();
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (WORKFLOW_STORAGE_PREFIXES.some((p) => k.startsWith(p) && !k.endsWith(today))) {
        toDelete.push(k);
      }
    }
    toDelete.forEach((k) => localStorage.removeItem(k));
  }, []);

  // activeStore가 바뀌면(계정 전환·SUPER 매장 전환) 모든 콜백이 새 매장의 키를 가리키도록
  // 재생성돼야 하므로, 반환 객체 identity도 activeStore에 의존한다.
  return useMemo(() => ({
    readUpdateDone, readNaverDone, readPendingIds, readStoreUnresolved,
    saveUpdateDone, saveNaverDone, savePendingIds, saveStoreUnresolved,
    pruneOldKeys,
  }), [
    readUpdateDone, readNaverDone, readPendingIds, readStoreUnresolved,
    saveUpdateDone, saveNaverDone, savePendingIds, saveStoreUnresolved,
    pruneOldKeys,
  ]);
}